import {RouterModule, Routes} from '@angular/router';
import {NgModule} from '@angular/core';
import {LayoutComponent} from './secure/layout/layout.component';
import {LoginComponent} from './public/login/login.component';
import {SignUpComponent} from './public/signup/sign-up.component';
import {SignupMode} from './public/signup/signup-mode/signup-mode.enum';
import {VerifyEmailComponent} from './public/signup/verify-email/verify-email.component';
import {RegisterAnonymousUserComponent} from './public/register-anonymous-user/register-anonymous-user.component';
import {PaymentRequestListComponent} from './secure/payment/payment-request-list/payment-request-list.component';
import {EcommercePaymentDashboardComponent} from './secure/payment/ecommerce-payment-dashboard/ecommerce-payment-dashboard.component';
import {RefundTransactionsComponent} from './secure/payment/refund-transactions/refund-transactions.component';
import {EcommerceDashboardComponent} from './secure/ecommerce/dashboard/ecommerce-dashboard.component';
import {FloatMonitoringComponent} from './secure/ecommerce/float-monitoring/float-monitoring.component';
import {RefundDashboardComponent} from './secure/ecommerce/refund-dashboard/refund-dashboard.component';
import {PaymentRequestCreateComponent} from './secure/payment/payment-request-create/payment-request-create.component';
import {PaymentRequestViewComponent} from './secure/payment/payment-request-view/payment-request-view.component';
import {PaymentRequestSendComponent} from './secure/payment/payment-request-send/payment-request-send.component';
import {PaymentRequestEditComponent} from './secure/payment/payment-request-edit/payment-request-edit.component';
import {UserEditComponent} from './secure/user/user-edit/user-edit.component';
import {AccountSettingsComponent} from './secure/account-settings/account-settings.component';
import {AccountRecoveryComponent} from './public/account-recovery/account-recovery.component';
import {PasswordResetComponent} from './public/password-reset/password-reset.component';
import {UserInvitationComponent} from './secure/user/user-invitation/user-invitation.component';
import {HomeComponent} from './secure/home/home.component';
import {EcommerceLandingComponent} from './secure/ecommerce-landing/ecommerce-landing.component';
import {EcommerceKycWizardComponent} from './secure/ecommerce-kyc/ecommerce-kyc-wizard.component';
import {BatchListComponent} from './secure/settlement/batch/batch-list/batch-list.component';
import {BatchViewComponent} from './secure/settlement/batch/batch-view/batch-view.component';
import {RoleGuardService} from './sdk/service/role-guard.service';
import {ROUTES} from './app.routes';
import {AuthGuardService} from './sdk/service/auth-guard.service';
import {EcommerceGuardService} from './sdk/service/ecommerce-guard.service';
import {UserInvitationListComponent} from './secure/user/user-invitation-list/user-invitation-list.component';
import {UserListComponent} from './secure/user/user-list/user-list.component';
import {BulkPaymentRequestComponent} from './secure/payment/bulk-payment-request/bulk-payment-request.component';
import {PaymentRequestUploadComponent} from './secure/payment/payment-request-upload/payment-request-upload.component';
import {InvoicesComponent} from './secure/invoices/invoices.component';
import {MessageBundlesComponent} from './secure/purchase/message-bundles/message-bundles.component';
import {environment} from '../environments/environment';
import {QrDashboardComponent} from './secure/qr/qr.dashboard.component';
import {EditQrCodeComponent} from './secure/qr/edit-qr-code/edit-qr-code.component';
import {QrCodeDetailsComponent} from './secure/qr/qr-code-details/qr-code-details.component';
import {QrPaymentsDashboardComponent} from './secure/qr/qr-payments-dashboard/qr-payments-dashboard.component';
import {FaqsComponent} from './public/faqs/faqs.component';
import {InvoiceComponent} from './public/invoice/invoice.component';
import {FeesComponent} from './secure/fees/fees.component';

class TitleCreator
{
    public static pageTitle(path: string): string
    {
        return environment.instanceName + ' | ' + path;
    }
}

const PUBLIC_ROUTES: Routes = [
    {path: 'public/login', component: LoginComponent, title: TitleCreator.pageTitle('Login')},
    {
        path: 'public/user/register',
        component: RegisterAnonymousUserComponent,
        title: TitleCreator.pageTitle('Register')
    },
    {
        path: 'public/passwordreset',
        component: PasswordResetComponent,
        title: TitleCreator.pageTitle('Password Reset')
    },
    {
        path: 'public/accountrecovery',
        component: AccountRecoveryComponent,
        title: TitleCreator.pageTitle('Account Recovery')
    },
    {
        path: 'public/faqs',
        component: FaqsComponent,
        title: TitleCreator.pageTitle('Frequently Asked Questions')
    },
    {
        path: 'public/invoice/:invoiceToken',
        component: InvoiceComponent,
        title: TitleCreator.pageTitle('Download Invoice')
    },
    {
        path: 'public/signup',
        component: SignUpComponent, title: TitleCreator.pageTitle('Sign Up'),
    },
    ...(environment.eCommerceEnabled ? [
        {
            path: 'public/signup/ecommerce',
            component: SignUpComponent, title: TitleCreator.pageTitle('Sign Up'),
            data: {
                mode: SignupMode.TRIMMED
            }
        },
        {
            path: 'public/signup/verify-email',
            component: VerifyEmailComponent, title: TitleCreator.pageTitle('Verify Email')
        }
    ] : []),
    {
        path: 'public/signup/walkthrough',
        component: SignUpComponent, title: TitleCreator.pageTitle('Walkthrough'),
        data: {
            lastStepIndex: 4,
            walkthrough: true
        }
    },
    {
        path: 'public/signup/:integratorUuid',
        component: SignUpComponent, title: TitleCreator.pageTitle('Sign Up'),
    },
    {
        path: 'public/signup/:merchantIdentifier/:integratorPrefix/:integratorUuid',
        component: SignUpComponent, title: TitleCreator.pageTitle('Sign Up'),
    },
    {path: '**', redirectTo: 'public/login', pathMatch: 'full'}
];

const SECURE_ROUTES: Routes = [
    {
        path: 'secure', component: LayoutComponent, children: [
            {
                path: 'payment/list',
                title: TitleCreator.pageTitle('Payment List'),
                component: PaymentRequestListComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.ACCOUNT.LIST.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().INDIVIDUAL_DASHBOARD, contentHeaderLogo: 'billpay'}
                }
            },
            {
                path: 'payment/ecommerce',
                title: TitleCreator.pageTitle('E-Commerce Transactions'),
                component: EcommercePaymentDashboardComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.ACCOUNT.ECOMMERCE.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().ECOMMERCE_TRANSACTIONS, contentHeaderLogo: 'ecommerce'}
                }
            },
            {
                path: 'payment/refunds',
                title: TitleCreator.pageTitle('Refund Transactions'),
                component: RefundTransactionsComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.ACCOUNT.REFUND_TRANSACTIONS.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().REFUND_TRANSACTIONS, contentHeaderLogo: 'ecommerce'}
                }
            },
            {
                path: 'ecommerce/dashboard',
                title: TitleCreator.pageTitle('E-Commerce Dashboard'),
                component: EcommerceDashboardComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.ACCOUNT.ECOMMERCE_DASHBOARD.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().ECOMMERCE_DASHBOARD, contentHeaderLogo: 'ecommerce'}
                }
            },
            {
                path: 'ecommerce/refund-dashboard',
                title: TitleCreator.pageTitle('Refund Dashboard'),
                component: RefundDashboardComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.ACCOUNT.REFUND_DASHBOARD.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().REFUND_DASHBOARD, contentHeaderLogo: 'ecommerce'}
                }
            },
            {
                path: 'ecommerce/float-monitoring',
                title: TitleCreator.pageTitle('Float Monitoring'),
                component: FloatMonitoringComponent,
                canActivate: [AuthGuardService, RoleGuardService, EcommerceGuardService],
                data: {
                    permissions: [ROUTES.SECURE.ACCOUNT.ECOMMERCE_FLOAT_MONITORING.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().FLOAT_MONITORING}
                }
            },
            {
                path: 'payment/bulk',
                title: TitleCreator.pageTitle('Bulk Payment List'),
                component: BulkPaymentRequestComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.ACCOUNT.BULK.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().BULK_DASHBOARD, contentHeaderLogo: 'billpay'}
                }
            },
            {
                path: 'payment/create',
                title: TitleCreator.pageTitle('Payment Create'),
                component: PaymentRequestCreateComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [
                        ROUTES.SECURE.ACCOUNT.CREATE.privilege,
                        ROUTES.SECURE.ACCOUNT.READONLY_CREATE.privilege
                    ],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().CREATE_INVOICE, contentHeaderLogo: 'billpay',
                        pageHeaderTooltipText: 'This is where you create a request to pay transaction, invoice and ' +
                            'payment link to send to a client',
                        pageHeaderCentered: true
                    }
                }
            },
            {
                path: 'payment/create/quick-pay',
                title: TitleCreator.pageTitle('Quick Pay'),
                component: PaymentRequestCreateComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [
                        ROUTES.SECURE.ACCOUNT.QUICK_PAY_CREATE.privilege,
                        ROUTES.SECURE.ACCOUNT.READONLY_CREATE.privilege
                    ],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().QUICK_PAY, contentHeaderLogo: 'billpay',
                        pageHeaderTooltipText: 'This is where you create a request to pay transaction and payment link ' +
                            'to send to a client',
                        pageHeaderCentered: true
                    }
                }
            },
            {
                path: 'payment/view/:id',
                title: TitleCreator.pageTitle('Payment View'),
                component: PaymentRequestViewComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.ACCOUNT.VIEW.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().REQUEST_TO_PAY_DETAILS, contentHeaderLogo: 'billpay'}
                }
            },
            {
                path: 'payment/send/:id',
                title: TitleCreator.pageTitle('Payment Send'),
                component: PaymentRequestSendComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.ACCOUNT.SEND.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().SEND_REQUEST_TO_PAY_TRANSACTION, contentHeaderLogo: 'billpay',
                        pageHeaderCentered: true
                    }
                }
            },
            {
                path: 'payment/send/quick-pay/:id',
                title: TitleCreator.pageTitle('Quick Pay Send'),
                component: PaymentRequestSendComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.ACCOUNT.QUICK_PAY_SEND.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().SEND_QUICK_PAY, contentHeaderLogo: 'billpay',
                        pageHeaderCentered: true
                    }
                }
            },
            {
                path: 'payment/edit/:id',
                title: TitleCreator.pageTitle('Payment Edit'),
                component: PaymentRequestEditComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.ACCOUNT.EDIT.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().REQUEST_TO_PAY_TRANSACTION_EDIT, contentHeaderLogo: 'billpay',
                        pageHeaderTooltipText: 'This is where you can edit existing request to pay transaction that have' +
                            ' not yet been paid',
                        pageHeaderCentered: true
                    }
                }
            },
            {
                path: 'payment/upload/requests',
                title: TitleCreator.pageTitle('Bulk Upload'),
                component: PaymentRequestUploadComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [
                        ROUTES.SECURE.ACCOUNT.UPLOAD_REQUESTS_FILE.privilege,
                        ROUTES.SECURE.ACCOUNT.READONLY_UPLOAD_REQUESTS_FILE.privilege
                    ],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().UPLOAD_BULK_FILE, contentHeaderLogo: 'billpay',
                        pageHeaderTooltipText: 'Upload request to pay transactions for outstanding balances in bulk, ' +
                            'using a comma-separated value (CSV) formatted file.'
                    }
                }
            },
            {
                path: 'user',
                title: TitleCreator.pageTitle('User List'),
                component: UserListComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.USER.LIST.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().USERS,
                        pageHeaderTooltipText: 'This is where you can view users and edit their details'
                    }
                }
            },
            {
                path: 'user/create',
                title: TitleCreator.pageTitle('User Create'),
                component: UserInvitationComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.USER.CREATE.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().USER_INVITATION,
                        pageHeaderTooltipText: 'Invite a person to use the system and assign them an organizational ' +
                            'role. They will be added under your current organization.',
                        pageHeaderCentered: true
                    }
                }
            },
            {
                path: 'user/edit/:id',
                title: TitleCreator.pageTitle('User Edit'),
                component: UserEditComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.USER.EDIT.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().USER_EDIT,
                        pageHeaderTooltipText: 'Change a user\'s details, role or configure their access to the system',
                        pageHeaderCentered: true
                    }
                }
            },
            {
                path: 'user/invitation',
                title: TitleCreator.pageTitle('User Invitation'),
                component: UserInvitationListComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.USER.INVITATION.LIST.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().USER_INVITATIONS,
                        pageHeaderTooltipText: 'This is where you can add new users and assign different roles to them'
                    }
                }
            },
            {
                path: 'business/settings',
                title: TitleCreator.pageTitle('Account Settings'),
                component: AccountSettingsComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.BUSINESS.EDIT.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().ACCOUNT_SETTINGS,
                        pageHeaderCentered: true
                    }
                }
            },
            {
                path: 'settlement/batch',
                title: TitleCreator.pageTitle('Settlement List'),
                component: BatchListComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.SETTLEMENT_BATCH.LIST.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().SETTLEMENT}
                }
            },
            {
                path: 'settlement/batch/:id',
                title: TitleCreator.pageTitle('Settlement View'),
                component: BatchViewComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.SETTLEMENT_BATCH.VIEW.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().SETTLEMENT_BATCH}
                }
            },
            {
                path: 'invoices',
                title: TitleCreator.pageTitle('Invoice List'),
                component: InvoicesComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.INVOICES.LIST.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().INVOICES, contentHeaderLogo: 'billpay'}
                }
            },
            {
                path: 'purchase/messageBundles',
                title: TitleCreator.pageTitle('Message Bundles'),
                component: MessageBundlesComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.PURCHASE.MESSAGE_BUNDLES.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().PURCHASE,
                        pageHeaderTooltipText: 'Purchase SMS & Email bundles'
                    }
                }
            },
            {
                path: 'fees',
                title: TitleCreator.pageTitle('Fees'),
                component: FeesComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.FEES.VIEW.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().FEES,
                        pageHeaderTooltipText: 'Fees'
                    }
                }
            },
            {
                path: 'home',
                title: TitleCreator.pageTitle('Home'),
                component: HomeComponent,
                canActivate: [AuthGuardService]
            },
            ...(environment.eCommerceEnabled ? [
                {
                    path: 'ecommerce/landing',
                    title: TitleCreator.pageTitle('Welcome'),
                    component: EcommerceLandingComponent,
                    canActivate: [AuthGuardService, EcommerceGuardService],
                    data: {
                        pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().ECOMMERCE_LANDING}
                    }
                },
                {
                    path: 'ecommerce/kyc',
                    title: TitleCreator.pageTitle('Verify Your Business'),
                    component: EcommerceKycWizardComponent,
                    canActivate: [AuthGuardService, EcommerceGuardService],
                    data: {
                        pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().ECOMMERCE_KYC}
                    }
                },
                {
                    path: 'ecommerce/kyc/view',
                    title: TitleCreator.pageTitle('Verify Your Business'),
                    component: EcommerceKycWizardComponent,
                    canActivate: [AuthGuardService, EcommerceGuardService],
                    data: {
                        readOnly: true,
                        pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().ECOMMERCE_KYC}
                    }
                }
            ] : []),
            {
                path: 'qr',
                title: TitleCreator.pageTitle('QR Codes'),
                component: QrDashboardComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.QR.LIST.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().QR_CODES_DASHBOARD, contentHeaderLogo: 'billpay'}
                }
            },
            {
                path: 'qr/payments',
                title: TitleCreator.pageTitle('QR Payments'),
                component: QrPaymentsDashboardComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.QR.PAYMENTS.privilege],
                    pageHeaderSettings: {pageHeaderText: environment.getPageHeaders().QR_CODES_DASHBOARD, contentHeaderLogo: 'billpay'}
                }
            },
            {
                path: 'qr/create',
                title: TitleCreator.pageTitle('QR Code'),
                component: EditQrCodeComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.QR.CREATE.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().QR_CODE_CREATE, contentHeaderLogo: 'billpay',
                        pageHeaderCentered: true
                    }
                }
            },
            {
                path: 'qr/edit/:id',
                title: TitleCreator.pageTitle('QR Code Edit'),
                component: EditQrCodeComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.QR.EDIT.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().QR_CODE_EDIT, contentHeaderLogo: 'billpay',
                        pageHeaderCentered: true
                    }
                }
            },
            {
                path: 'qr/details/:id',
                title: TitleCreator.pageTitle('QR Code Details'),
                component: QrCodeDetailsComponent,
                canActivate: [AuthGuardService, RoleGuardService],
                data: {
                    permissions: [ROUTES.SECURE.QR.DETAILS.privilege],
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().QR_CODE_DETAILS, contentHeaderLogo: 'billpay',
                        pageHeaderCentered: true
                    }
                }
            },
            {
                path: 'walkthrough',
                loadComponent: () => import('./public/product-walkthrough/product-walkthrough.component').then((m) => m.ProductWalkthroughComponent),
                canActivate: [AuthGuardService],
                title: TitleCreator.pageTitle('Walkthrough'),
                data: {
                    pageHeaderSettings: {
                        pageHeaderText: environment.getPageHeaders().WALKTHROUGH_HEADER
                    }
                }
            },
        ]
    }
];

@NgModule({
    imports: [
        RouterModule.forChild(SECURE_ROUTES),
        RouterModule
            .forRoot(
                PUBLIC_ROUTES,
                {onSameUrlNavigation: 'reload', anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled'})
    ],
    exports: [RouterModule]
})
export class UIRoutes
{
}
